import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../lib/auth-store';
import { GoogleSignInButton } from '../components/auth/GoogleSignIn';
import useTenantBrandStore from '../lib/tenant-brand-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CreditCard, Eye, EyeOff, CheckCircle2, Headphones, Users, TrendingUp, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const CITY_IMAGES = [
  { src: '/cities/chicago.jpg', city: 'Chicago, IL' },
  { src: '/cities/houston.jpg', city: 'Houston, TX' },
  { src: '/cities/atlanta.jpg', city: 'Atlanta, GA' },
  { src: '/cities/los-angeles.jpg', city: 'Los Angeles, CA' },
  { src: '/cities/miami.jpg', city: 'Miami, FL' },
];

const DEMO_ROLES = [
  { value: 'iso', label: 'ISO Owner', description: 'See the full admin + team management view' },
  { value: 'iso_agent', label: 'ISO Agent', description: 'See the sales rep view with pipeline & leads' },
  { value: 'iso_subagent', label: 'Subagent', description: 'See the limited agent view' },
];

// ── Test Drive Modal ───────────────────────────────────────────────────────────
function TestDriveModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', role: 'iso' });
  const [step, setStep] = useState('form'); // form | sent
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.requestDemoAccess(form);
      setStep('sent');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10,10,20,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, boxShadow: '0 24px 80px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#1e1b4b 0%,#312e81 100%)', padding: '28px 32px 24px', position: 'relative' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center' }}>
            <X size={16} />
          </button>
          <img src="/isocntrl-logo-400.png" alt="ISOCNTRL" style={{ height: 36, marginBottom: 12 }} />
          <h2 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: '0 0 6px' }}>Explore a Live Demo</h2>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, margin: 0 }}>Tell us who you are and we'll give you the exact view for your role.</p>
        </div>

        {step === 'form' ? (
          <form onSubmit={handleSubmit} style={{ padding: '28px 32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Full Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Jane Smith"
                  required
                  data-testid="demo-name-input"
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="jane@company.com"
                  required
                  data-testid="demo-email-input"
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="(555) 000-0000"
                  data-testid="demo-phone-input"
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Company</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  placeholder="Acme Payments"
                  data-testid="demo-company-input"
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Role selector */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>I am a… *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {DEMO_ROLES.map(r => (
                  <label
                    key={r.value}
                    data-testid={`demo-role-${r.value}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                      border: `2px solid ${form.role === r.value ? '#4f46e5' : '#e5e7eb'}`,
                      borderRadius: 10, cursor: 'pointer',
                      background: form.role === r.value ? '#f5f3ff' : '#fff',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input type="radio" name="role" value={r.value} checked={form.role === r.value} onChange={() => setForm(f => ({ ...f, role: r.value }))} style={{ accentColor: '#4f46e5' }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{r.label}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{r.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {error && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: '#fef2f2', borderRadius: 8 }}>{error}</p>}

            <button
              type="submit"
              disabled={loading}
              data-testid="demo-submit-btn"
              style={{ width: '100%', padding: '13px', background: loading ? '#a5b4fc' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', transition: 'background 0.15s' }}
            >
              {loading ? 'Sending…' : 'Send me the demo link'}
            </button>
            <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 10 }}>We'll send a confirmation email — no spam, ever.</p>
          </form>
        ) : (
          <div style={{ padding: '40px 32px', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1e1b4b', margin: '0 0 10px' }}>Check your inbox!</h3>
            <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
              We sent a confirmation link to <strong>{form.email}</strong>.<br />
              Click it to enter your personalized demo.
            </p>
            <p style={{ color: '#9ca3af', fontSize: 12 }}>Link expires in 24 hours. Check spam if you don't see it.</p>
            <button onClick={onClose} style={{ marginTop: 20, background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyMfa, mfaRequired, user, lockoutSeconds, lockoutMessage } = useAuthStore();
  const { branding, isCustomTenant, fetchBranding, loaded: brandLoaded } = useTenantBrandStore();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [lockoutTimer, setLockoutTimer] = useState(0);
  const [mfaMethod, setMfaMethod] = useState(null);
  const [phoneMasked, setPhoneMasked] = useState('');
  const [showTestDriveModal, setShowTestDriveModal] = useState(false);

  const handleTestDrive = () => setShowTestDriveModal(true);

  // Fetch tenant branding on mount (from subdomain or ?tenant= param)
  useEffect(() => {
    if (!brandLoaded) fetchBranding();
  }, [brandLoaded, fetchBranding]);

  const brandColor = branding.primary_color || '#3b82f6';

  // Sync lockout from store to local timer
  React.useEffect(() => {
    if (lockoutSeconds > 0) {
      setLockoutTimer(lockoutSeconds);
      if (lockoutMessage) setErrorMsg(lockoutMessage);
    }
  }, [lockoutSeconds, lockoutMessage]);

  // Lockout countdown
  React.useEffect(() => {
    if (lockoutTimer <= 0) return;
    const interval = setInterval(() => {
      setLockoutTimer(t => {
        if (t <= 1) { clearInterval(interval); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [lockoutTimer]);

  // City image carousel
  const [currentImage, setCurrentImage] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % CITY_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (lockoutTimer > 0) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const result = await login(credentials.email, credentials.password);

      if (result === 'locked') {
        // Directly set lockout timer from store state
        const state = useAuthStore.getState();
        if (state.lockoutSeconds > 0) {
          setLockoutTimer(state.lockoutSeconds);
          setErrorMsg(state.lockoutMessage || 'Account temporarily locked');
        }
        setLoading(false);
        return;
      }

      if (result === 'mfa_required') {
        const state = useAuthStore.getState();
        setMfaMethod(state.mfaMethod || 'totp');
        setPhoneMasked(state.mfaPhoneMasked || '');
        if (state.mfaMethod === 'sms') {
          toast.info('Verification code sent to your phone');
        } else if (state.mfaMethod === 'email') {
          toast.info('Verification code sent to your email');
        } else {
          toast.info('Enter your authenticator code');
        }
      } else if (result === true) {
        toast.success('Login successful');
        navigate('/');
      } else if (result === 'blocked') {
        const msg = useAuthStore.getState().error || 'This account cannot sign in yet.';
        setErrorMsg(msg);
        toast.error(msg);
      } else {
        setErrorMsg('Invalid email or password. Please try again.');
        toast.error('Login failed. Please check your credentials.');
      }
    } catch (error) {
      setErrorMsg(error.message || 'Login failed. Please try again.');
    }
    setLoading(false);
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      const success = await verifyMfa(mfaCode);
      if (success) {
        toast.success('Login successful');
        navigate('/');
      } else {
        setErrorMsg('Invalid MFA code. Please try again.');
      }
    } catch (error) {
      setErrorMsg('MFA verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendSms = async () => {
    try {
      const state = useAuthStore.getState();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/mfa/resend-sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_token: state.mfaToken }),
      });
      const out = await res.json().catch(() => ({}));
      if (res.ok && out.success !== false) toast.success('New code sent!');
      else toast.error(out.error || 'Failed to resend code');
    } catch { toast.error('Failed to resend'); }
  };

  return (
    <>
      {showTestDriveModal && <TestDriveModal onClose={() => setShowTestDriveModal(false)} />}
    <div className="min-h-screen flex">
      {/* Left Panel - City Image Carousel */}
      <div className="hidden lg:flex lg:w-[58%] relative overflow-hidden bg-[#0B0F19]">
        {/* Carousel Images */}
        {CITY_IMAGES.map((img, idx) => (
          <div
            key={img.city}
            className="absolute inset-0 transition-opacity duration-[1500ms] ease-in-out"
            style={{ opacity: idx === currentImage ? 1 : 0 }}
          >
            <img
              src={img.src}
              alt={img.city}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
        
        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0B0F19]/30" />
        
        {/* Centered Content */}
        <div className="relative z-10 flex flex-col items-center justify-end w-full p-12 pb-16">
          <div className="max-w-2xl w-full space-y-8">
            {/* Branding */}
            <div className="flex items-center space-x-4 mb-4">
              <img src="/isocntrl-logo-400.png" alt="ISOCNTRL" className="h-16 w-auto object-contain" data-testid="left-panel-logo" />
            </div>

            {/* Main Heading */}
            <div>
              <h1 className="text-4xl font-bold text-white mb-3 leading-tight">
                Your Merchant Services<br />Command Center
              </h1>
              <p className="text-lg text-white/70 font-light">
                Streamline onboarding. Automate underwriting. Close faster.
              </p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-3 gap-6">
              <div className="space-y-1.5">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm border border-white/10">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div className="text-2xl font-bold text-white">500K+</div>
                <div className="text-white/50 text-xs">Active Merchants</div>
              </div>
              <div className="space-y-1.5">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm border border-white/10">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div className="text-2xl font-bold text-white">99.2%</div>
                <div className="text-white/50 text-xs">Approval Rate</div>
              </div>
              <div className="space-y-1.5">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm border border-white/10">
                  <Headphones className="h-5 w-5 text-white" />
                </div>
                <div className="text-2xl font-bold text-white">24/7</div>
                <div className="text-white/50 text-xs">Support</div>
              </div>
            </div>

            {/* City indicator dots + name */}
            <div className="flex items-center justify-between pt-6 border-t border-white/10">
              <div className="flex items-center gap-2">
                {CITY_IMAGES.map((img, idx) => (
                  <button
                    key={img.city}
                    onClick={() => setCurrentImage(idx)}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      idx === currentImage ? 'w-8 bg-[#2563EB]' : 'w-1.5 bg-white/30 hover:bg-white/50'
                    }`}
                    data-testid={`city-dot-${idx}`}
                  />
                ))}
              </div>
              <span className="text-white/40 text-xs font-medium tracking-wider uppercase">
                {CITY_IMAGES[currentImage].city}
              </span>
            </div>

            {/* Partner Banks */}
            <div className="flex items-center gap-6 text-white/30 text-xs font-bold tracking-wider">
              <span>US BANK</span>
              <span>BMO HARRIS</span>
              <span>FIRST FRESNO</span>
              <span>AXIOM BANK</span>
              <span>AVIDIA BANK</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-12 bg-background">
        <div className="w-full max-w-[480px] space-y-8">
          {/* Logo & Branding — Dynamic per tenant */}
          <div className="text-center">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.company_name}
                className="h-20 w-auto mx-auto mb-6 object-contain"
                data-testid="tenant-login-logo"
              />
            ) : (
              <img
                src="/isocntrl-logo-400.png"
                alt="ISOCNTRL"
                className="h-20 w-auto mx-auto mb-6 object-contain"
                data-testid="tenant-login-logo"
              />
            )}
            <h1 className="text-4xl font-bold text-foreground mb-3" data-testid="tenant-login-name">
              {branding.company_name}
            </h1>
            <p className="text-muted-foreground text-base">
              {isCustomTenant ? 'Sign in to your portal' : 'Sign in to your account'}
            </p>
          </div>

          <div className="space-y-3">
            <GoogleSignInButton />
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-background text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          {/* Email/Password Form - Larger Inputs */}
          {lockoutTimer > 0 && (
            <div data-testid="lockout-timer" className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <div className="text-red-700 font-semibold text-lg">Account Locked</div>
              <div className="text-red-600 text-sm mt-1">Too many failed attempts</div>
              <div className="text-3xl font-mono text-red-800 mt-2">
                {Math.floor(lockoutTimer / 60)}:{(lockoutTimer % 60).toString().padStart(2, '0')}
              </div>
              <div className="text-xs text-red-500 mt-2">Please wait before trying again</div>
            </div>
          )}
          {mfaRequired ? (
            <form onSubmit={handleMfaVerify} className="space-y-5" data-testid="mfa-form">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold">Two-Factor Authentication</h3>
                {mfaMethod === 'sms' || mfaMethod === 'email' ? (
                  <p className="text-sm text-muted-foreground mt-1" data-testid="mfa-sent-to">
                    Code sent to <span className="font-mono font-medium">{phoneMasked || (mfaMethod === 'email' ? 'your email' : 'your phone')}</span>
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">Enter the 6-digit code from your authenticator app</p>
                )}
              </div>
              <div>
                <Input
                  type="text"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="h-14 text-2xl text-center tracking-[0.5em] font-mono"
                  maxLength={6}
                  autoFocus
                  data-testid="mfa-code-input"
                />
              </div>
              {errorMsg && <p className="text-sm text-red-500">{errorMsg}</p>}
              <Button type="submit" className="w-full h-12" disabled={loading || mfaCode.length !== 6} data-testid="mfa-verify-btn">
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              {(mfaMethod === 'sms' || mfaMethod === 'email') && (
                <button type="button" onClick={handleResendSms} className="w-full text-sm text-blue-600 hover:underline mt-2" data-testid="resend-sms-btn">
                  Didn&apos;t receive the code? Resend {mfaMethod === 'email' ? 'email' : 'SMS'}
                </button>
              )}
            </form>
          ) : lockoutTimer <= 0 && (
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-base">Email Address</Label>
              <div className="relative mt-2">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <Input
                  id="email"
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  placeholder="you@example.com"
                  className="pl-11 h-12 text-base"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between"><Label htmlFor="password" className="text-base">Password</Label><a href="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</a></div>
              <div className="relative mt-2">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                </div>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="••••••••"
                  className="pl-11 pr-11 h-12 text-base"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  className="w-4 h-4 rounded border-input"
                  data-testid="remember-me-checkbox"
                />
                <span className="text-sm text-muted-foreground">Remember me</span>
              </label>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base text-white"
              style={{ backgroundColor: brandColor }}
              disabled={loading}
              data-testid="sign-in-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>


            {errorMsg && (
              <div className="text-sm text-red-500 text-center bg-red-50 rounded-lg p-3" data-testid="login-error">
                {errorMsg}
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              className="w-full h-11 text-sm font-medium border-dashed"
              onClick={handleTestDrive}
              data-testid="test-drive-btn"
            >
              Test Drive — Explore a Live Demo
            </Button>
          </form>
          )}

          {/* Sign Up Link */}
          <div className="text-center text-sm">
            <span className="text-muted-foreground">Don&apos;t have an account? </span>
            <a href="/signup" className="hover:underline font-semibold" style={{ color: brandColor }} data-testid="create-account-link">
              Create ISO Account
            </a>
          </div>

          {/* Footer Links */}
          <div className="flex justify-center space-x-6 text-xs text-muted-foreground pt-6 border-t">
            <a href="#" className="hover:text-foreground">Terms of Service</a>
            <span>•</span>
            <a href="#" className="hover:text-foreground">Privacy Policy</a>
            <span>•</span>
            <a href="#" className="hover:text-foreground">Support</a>
          </div>

          {/* Partner Logos */}
          <div className="flex justify-center items-center space-x-6 pt-4 opacity-50">
            <div className="text-xs font-semibold tracking-wider text-muted-foreground">EPX</div>
            <div className="text-xs font-semibold tracking-wider text-muted-foreground">FISERVCOPILOT</div>
            <div className="text-xs font-semibold tracking-wider text-muted-foreground">ELAVON</div>
            <div className="text-xs font-semibold tracking-wider text-muted-foreground">TSYS-MSC</div>
          </div>

          {/* DEV MODE - Role Selector */}
          {user && (
            <div className="mt-8 p-6 border rounded-xl bg-accent/20">
              <h3 className="text-base font-semibold mb-4">Development Mode - Select Role</h3>
              <div className="space-y-2">
                {['Super Admin', 'ISO', 'ISO Agent', 'ISO Subagent'].map((role) => (
                  <button
                    key={role}
                    onClick={() => navigate('/')}
                    className="w-full p-3 text-left border rounded-lg hover:border-primary hover:bg-accent transition-colors"
                    data-testid={`role-${role.toLowerCase().replace(/ /g, '-')}`}
                  >
                    <div className="font-medium text-sm">{role}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default LoginPage;
